/**
 * Shared source of truth for "staff-bound" form fields — fields whose option
 * list is not stored statically but is loaded at render time from the org's
 * staff list (`GET /api/organization-setup/{cbo}/staff`).
 *
 * These live here (rather than inside a single form component) because more
 * than one renderer needs them: the bespoke OutreachActivityForm (activity_log
 * templates) and the generic FormInstanceEditor (event_detail / My Forms).
 * Before this, only OutreachActivityForm knew about them, so a staff-bound
 * field opened through My Forms rendered with no options — an empty picker
 * (TRUS-414).
 */
import React from 'react';

// The value of `options_source` that means "load this field's options from the
// org staff list". Mirrors OPTIONS_SOURCES in the backend and the CHECK added
// in migration 686.
export const OPTIONS_SOURCE_ORG_STAFF = 'org_staff';

// LEGACY fallback. Before migration 686 the only way to know a field was
// staff-bound was to recognise its NAME, which meant every new staff-bound
// field needed a frontend deploy and rendered as an empty picker until it
// shipped (TRUS-447). Fields now declare it in their own config via
// `options_source`; this set stays only so the renderer behaves identically on
// a template snapshot written BEFORE the migration ran — no deploy-order
// coupling in either direction. It is not a list to add to: give the field
// `options_source: 'org_staff'` instead.
//
// `training_attendees` was in this set once and is deliberately NOT any more:
// com_field 639 is type `int`, friendly name "Attendees" — a HEADCOUNT, not a
// list of people — and template 379 renders it as `number`. Being in this set
// was harmless only by accident (the number branch ignores options); the moment
// a template declared it as a select it would have turned a count box into a
// staff dropdown.
export const STAFF_BOUND_FIELDS = new Set<string>(['outreach_promotora', 'hub_staff_attendees']);

/**
 * True when this field's options are the org staff list.
 *
 * Prefers the field's own declared `options_source`; falls back to the legacy
 * name set for snapshots that predate migration 686.
 */
export function isStaffBound(fieldId: string, optionsSource?: string | null): boolean {
  if (optionsSource) return optionsSource === OPTIONS_SOURCE_ORG_STAFF;
  return STAFF_BOUND_FIELDS.has(fieldId);
}

// The subset that holds MANY people rather than one. These render as a
// multi-select people-picker; single-value staff fields (outreach_promotora)
// stay a plain dropdown.
export const MULTI_VALUE_FIELD_TYPES = new Set<string>(['checkbox', 'multiselect', 'multi_select']);

/**
 * True when this field should render as the staff people-picker — i.e. it is
 * staff-bound AND holds many people rather than one.
 *
 * `optionsSource` is optional so existing two-argument callers keep working;
 * without it this falls back to the legacy name set exactly as before.
 */
export function isStaffMultiSelect(
  fieldId: string,
  fieldType: string | undefined | null,
  optionsSource?: string | null,
): boolean {
  return isStaffBound(fieldId, optionsSource) && MULTI_VALUE_FIELD_TYPES.has(String(fieldType || ''));
}

export type StaffOption = { label: string; value: string };

/**
 * Loads the active org's staff list as `{label, value}` options for use as a
 * dynamic dropdown/checkbox source. Returns [] until loaded or on failure.
 */
export function useOrgStaffOptions(options?: { includeOrgAdmins?: boolean }): StaffOption[] {
  const includeOrgAdmins = options?.includeOrgAdmins === true;
  const [opts, setOpts] = React.useState<StaffOption[]>([]);
  React.useEffect(() => {
    const cboId = Number(localStorage.getItem('active_cbo_id') || 0);
    if (!cboId) return;
    // super_admin and ops_admin are PLATFORM roles — Trust/WeaveIn staff and
    // automation accounts, who are never the answer to "which of your
    // colleagues?". org_admin is different: it is a role inside the tenant,
    // held by real employees. It sits in this set because the first pickers to
    // use this hook (promotora, navigator) wanted frontline staff only.
    //
    // A MANAGER picker must not exclude them. At Amigos the two people with the
    // most direct reports — Maritza Maldonado with 13 and Don Aguilar with 4 —
    // are both org_admin, so filtering them out would drop 17 staff members'
    // managers from the list and leave their stored value unresolvable.
    const TRUST_ADMIN_ROLES = new Set(
      includeOrgAdmins ? ['super_admin', 'ops_admin'] : ['super_admin', 'ops_admin', 'org_admin'],
    );
    import('@/lib/authFetch').then(({ authFetchJson }) => {
      authFetchJson<any[]>(`/api/organization-setup/${cboId}/staff`)
        .then((rows) => {
          const filtered = (rows || [])
            .filter((r) => r.is_active !== false)
            .filter((r) => !TRUST_ADMIN_ROLES.has((r.role || '').toLowerCase()));
          setOpts(
            filtered.map((r) => {
              const id = String(r.user_id ?? r.id ?? '');
              const name =
                r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || id;
              return { label: name, value: id };
            }),
          );
        })
        .catch(() => setOpts([]));
    });
  }, [includeOrgAdmins]);
  return opts;
}
